import React, { useState } from 'react';
import {
  Page,
  CompactTable,
  useFetch,
  useFilters,
  usePageQuery,
  PermissionGuard,
  useUserinfo,
  RemoteSession,
  openNotificationWithIcon,
} from '@mss/mss-component-library';
import PageSidebar from 'jsx/common/FilterSidebar';
import Breadcrumbs from '../../../../common/Breadcrumbs';
import columns from './columns';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import RevokeAllSessionsButton from '../../../components/RevokeAllSessionsButton';
import { TFunction } from 'i18next';

const filterKeys: any[] = ['status', 'protocol'];
const searchKeys: any[] = ['asset_name', 'user_name', 'status', 'protocol'];

const customHeadingLabel = (t: TFunction) => (key: string) => {
  if (key === 'user_name') return t('User');
  if (key === 'asset_name') return t('Target Asset');
  if (key === 'profile_name') return t('Profile');
  if (key === 'status') return t('Status');
  return key;
};

const customOptionLabel = (t: TFunction) => (key: string, value: any) => {
  if (key === 'status') {
    switch (value) {
      case 'inactive':
        return t('Inactive');
      case 'active':
        return t('Active');
      case 'denied':
        return t('Denied');
      case 'revoked':
        return t('Revoked');
      case 'expired':
        return t('Expired');
      case 'expired_unapproved':
        return t('Expired approval');
      default:
        return value;
    }
  }
  if (key === 'protocol') return value.toUpperCase();
  return value;
};

const PAGE_SIZE = 10;
type EnrichedSession = RemoteSession & {
  asset_name: string;
  user_name: string;
  profile_name: string;
};

export default function List() {
  const [hasPermission] = useUserinfo();
  const [sessions, setSessions] = useState<EnrichedSession[]>([]);
  const history = useHistory();
  const { url } = useRouteMatch();
  const [t] = useTranslation(['remoteAccess', 'common', 'asset', 'profile']);

  const [rawSessions, fetchSessions] = useFetch({
    url: '/api/remote-session/?limit=1000',
    defaultValue: [],
    initialFetch: hasPermission('admin_view_remotesession'),
    onSucces: async ({ data }) => {
      // Note: api response has a different structure with the pagination enabled.
      let results = data.results;

      try {
        setSessions(
          results.map((x: EnrichedSession) => ({
            ...x,
            asset_name: x.asset_object.name,
            user_name: x.user_object.name,
          }))
        );
      } catch {
        openNotificationWithIcon(
          notification.error,
          t('Failed fetch sessions'),
          t(
            'Due to technical issues not all information could not be retrieved. Therefore you will only see the ids of the assets and users. Please try again later.'
          )
        );
      }
    },
  });

  const isFetching = rawSessions.loading;

  const [availableFilters, toggleFilter, filteredData, search] = useFilters(
    sessions,
    filterKeys,
    searchKeys,
    customOptionLabel(t),
    customHeadingLabel(t)
  );
  const [page, goToPage] = usePageQuery(filteredData.length / PAGE_SIZE);

  return (
    <PermissionGuard codename={'admin_view_remotesession'}>
      <Page
        breadCrumbs={<Breadcrumbs />}
        padding={false}
        actions={
          hasPermission('admin_update_remotesession') && (
            <RevokeAllSessionsButton fetchSessions={fetchSessions} />
          )
        }
        main={
          <PermissionGuard codename={'admin_view_remotesession'}>
            <CompactTable
              bordered={false}
              dataSource={filteredData}
              loading={isFetching}
              columns={columns(t, fetchSessions)}
              rowKey={'id'}
              onRow={(record: any) => ({
                onClick: () => history.push(`${url}/${record.id}`),
              })}
              pagination={{
                current: page,
                showQuickJumper: true,
                showSizeChanger: true,
                total: filteredData.length,
                showTotal: (total: number, range: any[]) =>
                  t('(RANGE1)-(RANGE2) of (TOTAL) items', {
                    range1: range[0],
                    range2: range[1],
                    TOTAL: total,
                  }),
              }}
              onChange={(pagination: any) => {
                if (page !== pagination.current) goToPage(pagination.current);
              }}
            />
          </PermissionGuard>
        }
        sidebar={
          <PermissionGuard codename={'admin_view_remotesession'}>
            <PageSidebar
              availableFilters={availableFilters}
              toggleFilter={toggleFilter}
              search={search}
            />
          </PermissionGuard>
        }
      />
    </PermissionGuard>
  );
}
